import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Shield, Info } from "lucide-react"
import type { SOC2Compliance } from "@/hooks/useReportActions"

interface SOC2ComplianceSectionProps {
  soc2Compliance: SOC2Compliance
}

export function SOC2ComplianceSection({ soc2Compliance }: SOC2ComplianceSectionProps) {
  const getScoreColor = (score: number) => {
    if (score >= 90) return "text-green-600"
    if (score >= 70) return "text-yellow-600"
    if (score >= 50) return "text-orange-600"
    return "text-red-600"
  }

  const sortedControls = Object.entries(soc2Compliance.technical_controls).sort(
    ([, a], [, b]) => a.score - b.score // Sort by score ascending (worst first - most insecure at top)
  )

  return (
    <div className="space-y-6">
      {/* Overall Score Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                SOC2 Technical Compliance
              </CardTitle>
              <CardDescription className="mt-2">
                Automated assessment of technical security controls
              </CardDescription>
            </div>
            <div className="text-right">
              <div className={`text-4xl font-bold ${getScoreColor(soc2Compliance.overall_technical_compliance_score)}`}>
                {soc2Compliance.overall_technical_compliance_score.toFixed(1)}%
              </div>
              <p className="text-sm text-muted-foreground mt-1">Overall Score</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Disclaimer */}
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {soc2Compliance.disclaimer}
            </AlertDescription>
          </Alert>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="border rounded-lg p-3">
              <p className="text-sm text-muted-foreground">Controls Scanned</p>
              <p className="text-2xl font-bold">{soc2Compliance.controls_scanned}</p>
            </div>
            <div className="border rounded-lg p-3">
              <p className="text-sm text-muted-foreground">Total Findings</p>
              <p className="text-2xl font-bold">{soc2Compliance.total_vulnerabilities}</p>
            </div>
            <div className="border rounded-lg p-3">
              <p className="text-sm text-muted-foreground">Controls Needing Review</p>
              <p className="text-2xl font-bold">{soc2Compliance.organizational_controls.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Technical Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Technical Controls Assessment</CardTitle>
          <CardDescription>
            Automated mapping to {Object.keys(soc2Compliance.technical_controls).length} scannable technical controls
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {sortedControls.map(([controlId, control]) => {
            // Determine background color based on score
            const getControlCardClass = (score: number) => {
              if (score >= 90) return "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800"
              if (score >= 70) return "bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800"
              if (score >= 50) return "bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800"
              return "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800"
            }

            const getBadgeClass = (score: number) => {
              if (score >= 90) return "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
              if (score >= 70) return "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200"
              if (score >= 50) return "bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200"
              return "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
            }

            return (
              <div
                key={controlId}
                className={`flex items-center justify-between p-3 rounded-lg border ${getControlCardClass(control.score)}`}
              >
                <div>
                  <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                    {control.id} - {control.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{control.description}</p>

                  {/* Severity Breakdown - Show inline if findings exist */}
                  {control.vulnerability_count > 0 && (
                    <div className="flex gap-2 flex-wrap mt-2">
                      {control.severity_breakdown.critical > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {control.severity_breakdown.critical} Critical
                        </Badge>
                      )}
                      {control.severity_breakdown.high > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {control.severity_breakdown.high} High
                        </Badge>
                      )}
                      {control.severity_breakdown.medium > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {control.severity_breakdown.medium} Medium
                        </Badge>
                      )}
                      {control.severity_breakdown.low > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {control.severity_breakdown.low} Low
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                <Badge className={getBadgeClass(control.score)}>
                  {control.score.toFixed(0)}%
                </Badge>
              </div>
            )
          })}
        </CardContent>
      </Card>

      {/* Organizational Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Organizational Controls - Manual Review Required</CardTitle>
          <CardDescription>
            These controls require documentation and policy review
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {soc2Compliance.organizational_controls.map((control) => (
            <div
              key={control.id}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div>
                <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                  {control.id} - {control.name}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  <span className="font-medium">Required:</span> {control.requires}
                </p>
              </div>
              <Badge className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                Manual
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
